import { useState } from "react";
import Modal from "./Modal";
import Button from "ui/buttons/Button";

## Basic Usage

export function BasicModal() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>Open Modal</Button>
      <Modal open={open} onClose={() => setOpen(false)} title="Basic Modal">
        <p>This is the modal content.</p>
      </Modal>
    </>
  );
}

<BasicModal />

---

## Sizes

export function ModalSizes() {
  const [size, setSize] = useState(null);
  return (
    <>
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <Button btnStyle="tertiary" onClick={() => setSize("small")}>Small</Button>
        <Button btnStyle="tertiary" onClick={() => setSize("medium")}>Medium</Button>
        <Button btnStyle="tertiary" onClick={() => setSize("large")}>Large</Button>
        <Button btnStyle="tertiary" onClick={() => setSize("full")}>Full</Button>
      </div>
      <Modal open={!!size} onClose={() => setSize(null)} title={`${size} Modal`} size={size || "medium"}>
        <p>This is a {size} sized modal.</p>
      </Modal>
    </>
  );
}

<ModalSizes />

---

## With Footer

export function ModalWithFooter() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>Open Modal with Footer</Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Confirm Action"
        footer={
          <>
            <Button btnStyle="tertiary" btnSize="large" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button btnSize="large" onClick={() => { alert("Confirmed!"); setOpen(false); }}>
              Confirm
            </Button>
          </>
        }
        variants={["bodyPadding", "footerButtons"]}
      >
        <p>Are you sure you want to proceed with this action?</p>
      </Modal>
    </>
  );
}

<ModalWithFooter />

---

## With Title Actions

export function ModalWithTitleActions() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>Open Modal with Title Actions</Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Settings"
        titleActions={
          <Button btnStyle="tertiary" btnSize="small" onClick={() => alert("Help clicked")}>
            Help
          </Button>
        }
        variants={["bodyPadding"]}
      >
        <p>Modal with an action button in the title bar.</p>
      </Modal>
    </>
  );
}

<ModalWithTitleActions />

---

## With Header

export function ModalWithHeader() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>Open Modal with Header</Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Document"
        header={
          <div style={{ padding: "0.5rem 1rem", backgroundColor: "#f1f5f9", borderBottom: "1px solid #e2e8f0" }}>
            <span style={{ fontSize: "0.875rem", color: "#64748b" }}>Last modified: Today at 3:45 PM</span>
          </div>
        }
        variants={["bodyPadding"]}
      >
        <p>Content below the header.</p>
      </Modal>
    </>
  );
}

<ModalWithHeader />

---

## Full Height

export function FullHeightModal() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>Open Full Height Modal</Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Full Height Modal"
        fullHeight
        variants={["bodyPadding"]}
      >
        <p>This modal stretches to full height.</p>
      </Modal>
    </>
  );
}

<FullHeightModal />

---

## Scrollable Content

export function ScrollableModal() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>Open Scrollable Modal</Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Terms & Conditions"
        scrollModal
        variants={["bodyPadding"]}
        footer={
          <>
            <Button btnStyle="tertiary" btnSize="large" onClick={() => setOpen(false)}>
              Decline
            </Button>
            <Button btnSize="large" onClick={() => setOpen(false)}>
              Accept
            </Button>
          </>
        }
      >
        {Array.from({ length: 20 }, (_, i) => (
          <p key={i}>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor
            incididunt ut labore et dolore magna aliqua.
          </p>
        ))}
      </Modal>
    </>
  );
}

<ScrollableModal />

---

## Prevent Close on Outside Click

export function NoCloseOnOutsideClick() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>Open Modal (No Outside Click Close)</Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Important Form"
        noCloseOnOutsideClick
        variants={["bodyPadding"]}
        footer={
          <Button btnSize="large" onClick={() => setOpen(false)}>
            Done
          </Button>
        }
      >
        <p>This modal can only be closed via the X button or the Done button.</p>
        <p>Clicking outside will not close it.</p>
      </Modal>
    </>
  );
}

<NoCloseOnOutsideClick />

---

## Submitting State

export function SubmittingModal() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = () => {
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setOpen(false);
    }, 2000);
  };
  return (
    <>
      <Button onClick={() => setOpen(true)}>Open Submitting Modal</Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Save Changes"
        submitting={submitting}
        variants={["bodyPadding", "footerButtons"]}
        footer={
          <>
            <Button btnStyle="tertiary" btnSize="large" onClick={() => setOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button btnSize="large" onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Saving..." : "Save"}
            </Button>
          </>
        }
      >
        <p>Click Save to see the submitting state.</p>
      </Modal>
    </>
  );
}

<SubmittingModal />

